import 'firebase/compat/firestore'

import { useEffect, useMemo, useState } from 'react'
import {
  useGDPRStatus,
  useInitializeUser,
  useSessionActions
} from 'domains/Session/hooks'

import PropTypes from 'prop-types'
import UserContext from './UserContext'
import firebase from 'firebase/compat/app'
import { useAuthState } from 'react-firebase-hooks/auth'
import { useHandleError } from 'hooks'
import useNewspaperConfig from 'contexts/NewspaperConfig/useNewspaperConfig'

const UserProvider = ({ children }) => {
  /* The above code is a function that takes in a callback function as an argument.
  The callback function is then called with the error as an argument. */
  const handleError = useHandleError()

  /* Using the useGDPRStatus hook to get the GDPR status of the user. */
  const gdpr = useGDPRStatus()

  /* Using the useAuthState hook to get the user from the firebase auth state. */
  const [auth] = useAuthState(firebase.auth())

  /* Getting newspaper id from newspaper config */
  const { newspaperId } = useNewspaperConfig()

  /* Using the useInitializeUser hook to get the user from the firebase auth state. */
  const [user, loading, error] = useInitializeUser()

  /* Create a state for user`s points */
  const [points, setPoints] = useState(0)

  // Session methods
  const {
    updateEmailVerificationStatus,
    getLastSessionFromLocalStorage,
    setSessionToLocalStorage,
    updateGDPRStatus
  } = useSessionActions()

  // Updating user's email verification status
  useEffect(() => {
    // Check if data is loaded
    const isUserDataLoaded = auth && user && !loading

    // Updating email verification status
    isUserDataLoaded &&
      !user.emailVerified &&
      updateEmailVerificationStatus({
        id: auth.uid,
        sessionUserEmailVerified: auth?.emailVerified,
        dbUserEmailVerified: user?.emailVerified,
        onError: handleError
      })
  }, [auth, updateEmailVerificationStatus, user, loading, handleError])

  // Updating LS with user session info
  useEffect(() => {
    const isUserDataLoaded = auth && user && !loading
    if (isUserDataLoaded) {
      const lastSession = getLastSessionFromLocalStorage()
      lastSession?.email !== auth?.email &&
        /* Set the session to the user's local storage. */
        setSessionToLocalStorage({
          email: auth.email,
          avatarUrl: auth.photoURL,
          provider: localStorage.getItem('lastSessionProvider')
        })
    }
  }, [
    auth,
    user,
    loading,
    getLastSessionFromLocalStorage,
    setSessionToLocalStorage
  ])

  // Updating user's points
  useEffect(() => {
    const isUserDataLoaded = auth && user && !loading && newspaperId
    if (isUserDataLoaded) {
      const userPoints = user?.pointsGiven?.[newspaperId] || 0
      setPoints(userPoints)
    } else {
      setPoints(0)
    }
  }, [auth, user, loading, newspaperId])

  // Update GDPR status
  useEffect(() => {
    /* This is a ternary operator. It is checking if the user, value, and loading variables are all
    true. If they are, it will return true. Otherwise, it will return false. */
    const isUserDataLoaded = auth && user && !loading

    /* If the user is logged in, and the user's data is loaded, and the user's data has not yet been
    updated to reflect the new GDPR status, then update the user's data to reflect the new GDPR
    status. */
    if (isUserDataLoaded && !user?.gdpr && gdpr !== user?.gdpr)
      updateGDPRStatus({ id: auth?.uid, gdpr, onError: handleError })
  }, [gdpr, auth, user, updateGDPRStatus, handleError, loading])

  // Handling user fetching error
  useEffect(() => {
    error && handleError(error)
  }, [error, handleError])

  // Preparing context
  const contextData = useMemo(() => {
    const isUserLoaded = user && !loading
    return { user: user, loading, loaded: isUserLoaded, points, setPoints }
  }, [loading, user, points])

  return (
    <UserContext.Provider value={contextData}>{children}</UserContext.Provider>
  )
}

UserProvider.propTypes = { children: PropTypes.node }

export default UserProvider
